/**
 * CorrelationPage — Correlation scanner & pair trading tool.
 *
 * Sections:
 *   1. Ticker input + period/threshold controls
 *   2. Correlation heatmap (NxN matrix)
 *   3. Highly correlated pairs list (click to inspect divergence)
 *   4. Divergence chart: spread z-score with ±2σ signal lines
 *   5. Sector correlation matrix
 */
import { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import { GitCompare, Loader2, RefreshCw, Search, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { clsx } from 'clsx'
import { useCorrelation } from '@/hooks/useCorrelation'
import { navigateToTerminalCommand } from '@/lib/navigation'
import { useNavigate, useSearchParams } from 'react-router-dom'

// ── Helpers ───────────────────────────────────────────────────────────────────

function corrColor(v: number | null): string {
  if (v === null) return 'bg-zinc-700/50 text-zinc-400'
  if (v >= 0.9)  return 'bg-red-700 text-white'
  if (v >= 0.75) return 'bg-red-500/80 text-white'
  if (v >= 0.5)  return 'bg-orange-500/70 text-white'
  if (v >= 0.25) return 'bg-yellow-600/60 text-white'
  if (v >= 0)    return 'bg-zinc-600 text-zinc-200'
  if (v >= -0.25) return 'bg-emerald-400/40 text-emerald-100'
  if (v >= -0.5)  return 'bg-emerald-500/70 text-white'
  return 'bg-emerald-700 text-white'
}

const PERIODS = ['1m', '3m', '6m', '1y', '2y'] as const
type Period = typeof PERIODS[number]

function SignalBadge({ signal }: { signal: string }) {
  if (signal === 'diverged_short_spread') {
    return (
      <span className="badge-red font-semibold">
        <TrendingDown size={11} /> SHORT SPREAD
      </span>
    )
  }
  if (signal === 'diverged_long_spread') {
    return (
      <span className="badge-green font-semibold">
        <TrendingUp size={11} /> LONG SPREAD
      </span>
    )
  }
  return (
    <span className="badge-neutral font-semibold">
      <Minus size={11} /> CONVERGED
    </span>
  )
}

// ── Z-Score sparkline (simple SVG) ────────────────────────────────────────────

function ZScoreSpark({
  data,
  width = 300,
  height = 80,
}: {
  data: { date: string; zscore: number }[]
  width?: number
  height?: number
}) {
  if (!data.length) return null
  const vals = data.map(d => d.zscore)
  const min = Math.min(...vals, -2.5)
  const max = Math.max(...vals, 2.5)
  const range = max - min || 1

  const toX = (i: number) => (i / (vals.length - 1)) * width
  const toY = (v: number) => height - ((v - min) / range) * height

  const path = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ')

  // ±2 reference lines
  const y2pos = toY(2).toFixed(1)
  const y2neg = toY(-2).toFixed(1)
  const y0    = toY(0).toFixed(1)

  const lastZ = vals[vals.length - 1]
  const lineColor = lastZ > 2 ? '#f43f5e' : lastZ < -2 ? '#10b981' : '#60a5fa'

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      style={{ height }}
      aria-label="Z-score over time"
    >
      {/* Zero line */}
      <line x1="0" y1={y0} x2={width} y2={y0} stroke="#6b7280" strokeWidth="0.5" strokeDasharray="3,3" />
      {/* +2 line */}
      <line x1="0" y1={y2pos} x2={width} y2={y2pos} stroke="#f43f5e" strokeWidth="0.8" strokeDasharray="4,4" />
      {/* -2 line */}
      <line x1="0" y1={y2neg} x2={width} y2={y2neg} stroke="#10b981" strokeWidth="0.8" strokeDasharray="4,4" />
      {/* Z-score path */}
      <path d={path} fill="none" stroke={lineColor} strokeWidth="1.5" />
      {/* Labels */}
      <text x="4" y={parseFloat(y2pos) - 3} fill="#f43f5e" fontSize="9">+2σ</text>
      <text x="4" y={parseFloat(y2neg) + 10} fill="#10b981" fontSize="9">-2σ</text>
    </svg>
  )
}

// ── Correlation heatmap ────────────────────────────────────────────────────────

function HeatmapCell({ value, isSelf }: { value: number | null; isSelf: boolean }) {
  return (
    <td
      className={clsx(
        'text-center text-[11px] font-mono p-1.5 transition-all',
        isSelf ? 'bg-blue/20 text-blue-light font-bold' : corrColor(value),
      )}
    >
      {isSelf ? '1.00' : value !== null ? value.toFixed(2) : '—'}
    </td>
  )
}

function CorrelationHeatmap() {
  const { matrix, matrixLoading, matrixError } = useCorrelation()

  if (matrixLoading) {
    return <div className="h-32 skeleton" />
  }
  if (matrixError) return <p className="text-rose-400 text-sm p-3">{matrixError}</p>
  if (!matrix) return <p className="text-text-muted text-sm p-3">Enter tickers above and click Compute.</p>

  const { tickers, matrix: mat } = matrix

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr>
            <th className="p-1.5 text-text-muted text-right w-16" />
            {tickers.map(t => (
              <th key={t} className="p-1.5 text-text-secondary font-mono text-center font-semibold w-14">
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickers.map((rowT, i) => (
            <tr key={rowT}>
              <td className="p-1.5 text-text-secondary font-mono text-right font-semibold pr-2">{rowT}</td>
              {mat[i].map((val, j) => (
                <HeatmapCell key={j} value={val} isSelf={i === j} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Pairs list ────────────────────────────────────────────────────────────────

function PairsList({ onSelectPair }: { onSelectPair: (t1: string, t2: string) => void }) {
  const { pairs, pairsLoading, pairsError } = useCorrelation()

  if (pairsLoading) {
    return <div className="h-24 skeleton" />
  }
  if (pairsError) return <p className="text-rose-400 text-sm p-3">{pairsError}</p>
  if (!pairs) return <p className="text-text-muted text-sm p-3">Click "Find Pairs" to scan the S&P 50 universe.</p>
  if (!pairs.pairs.length) return <p className="text-text-muted text-sm p-3">No pairs found above threshold.</p>

  return (
    <div className="space-y-1">
      <p className="text-xs text-text-muted mb-2">
        Found {pairs.total_found} pairs — showing top 20. Click a pair to analyse divergence.
      </p>
      <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {pairs.pairs.slice(0, 20).map((p) => (
          <button
            key={`${p.ticker1}-${p.ticker2}`}
            type="button"
            onClick={() => onSelectPair(p.ticker1, p.ticker2)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-surface-card
                       hover:bg-surface-hover border border-border/50 hover:border-blue/30
                       transition-colors text-left group"
          >
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold text-text-primary text-sm">{p.ticker1}</span>
              <span className="text-text-muted text-xs">/</span>
              <span className="font-mono font-semibold text-text-primary text-sm">{p.ticker2}</span>
            </div>
            <div className="flex items-center gap-3">
              <div
                className={clsx(
                  'w-16 h-1.5 rounded-full',
                  p.correlation >= 0.95 ? 'bg-red-500' : 'bg-orange-500',
                )}
                style={{ opacity: p.correlation }}
              />
              <span className={clsx(
                'font-mono text-sm font-semibold',
                p.correlation >= 0.95 ? 'text-red-400' : 'text-orange-400',
              )}>
                {p.correlation.toFixed(3)}
              </span>
              <GitCompare
                size={13}
                className="text-text-muted group-hover:text-blue transition-colors"
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Divergence panel ──────────────────────────────────────────────────────────

function DivergencePanel() {
  const { divergence, divergenceLoading, divergenceError, pairTicker1, pairTicker2 } = useCorrelation()
  const navigate = useNavigate()

  if (divergenceLoading) {
    return <div className="h-24 skeleton" />
  }
  if (divergenceError) return <p className="text-rose-400 text-sm p-3">{divergenceError}</p>
  if (!divergence) return (
    <p className="text-text-muted text-sm p-3">
      Select a pair from the list above, or click "Find Pairs" then choose a row.
    </p>
  )

  const { correlation, zscore, signal, trade_suggestion, zscore_history } = divergence

  return (
    <div className="space-y-4">
      {/* Key metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Pair', value: `${pairTicker1} / ${pairTicker2}` },
          { label: 'Correlation', value: correlation !== null ? correlation.toFixed(4) : 'N/A' },
          { label: 'Z-Score', value: zscore !== null ? zscore.toFixed(4) : 'N/A' },
          { label: 'Signal', value: <SignalBadge signal={signal} /> },
        ].map(({ label, value }) => (
          <div key={label} className="card p-3">
            <p className="text-xs text-text-muted mb-1">{label}</p>
            <div className="text-sm font-semibold text-text-primary font-mono">{value}</div>
          </div>
        ))}
      </div>

      {/* Trade suggestion */}
      <div className={clsx(
        'rounded-lg border px-4 py-3 text-sm',
        signal === 'diverged_short_spread' ? 'border-rose-500/30 bg-rose-500/10 text-rose-300'
        : signal === 'diverged_long_spread' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
        : 'border-border bg-surface-card text-text-secondary',
      )}>
        {trade_suggestion}
      </div>

      {/* Z-score chart */}
      {zscore_history.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="panel-title">
              Z-Score History
            </p>
            <div className="flex items-center gap-4 text-[10px] text-text-muted">
              <span className="flex items-center gap-1"><span className="w-4 border-t-2 border-dashed border-rose-500 inline-block" /> +2σ sell</span>
              <span className="flex items-center gap-1"><span className="w-4 border-t-2 border-dashed border-emerald-500 inline-block" /> -2σ buy</span>
            </div>
          </div>
          <ZScoreSpark data={zscore_history} height={80} />
        </div>
      )}

      {/* Terminal shortcuts */}
      <div className="flex gap-2 text-xs">
        <button
          type="button"
          onClick={() => navigateToTerminalCommand(navigate, `/corr ${pairTicker1} ${pairTicker2}`)}
          className="px-2 py-1 rounded bg-surface-card border border-border text-text-muted hover:text-text-primary hover:border-blue/30 transition-colors font-mono"
        >
          /corr {pairTicker1} {pairTicker2}
        </button>
        <button
          type="button"
          onClick={() => navigateToTerminalCommand(navigate, `/divergence ${pairTicker1} ${pairTicker2}`)}
          className="px-2 py-1 rounded bg-surface-card border border-border text-text-muted hover:text-text-primary hover:border-blue/30 transition-colors font-mono"
        >
          /divergence {pairTicker1} {pairTicker2}
        </button>
      </div>
    </div>
  )
}

// ── Sector correlation ────────────────────────────────────────────────────────

function SectorCorrelationMatrix() {
  const { sectorMatrix, sectorLoading, sectorError } = useCorrelation()

  if (sectorLoading) {
    return <div className="h-40 skeleton" />
  }
  if (sectorError) return <p className="text-rose-400 text-sm p-3">{sectorError}</p>
  if (!sectorMatrix) return <p className="text-text-muted text-sm p-3">Loading…</p>

  const { tickers, labels, matrix } = sectorMatrix

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="p-1 text-text-muted text-right w-28" />
            {tickers.map((t, i) => (
              <th key={t} className="p-1 text-text-secondary font-mono text-center font-semibold w-10"
                  title={labels[i]}>
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickers.map((rowT, i) => (
            <tr key={rowT}>
              <td className="p-1 text-text-secondary text-right pr-2 font-semibold truncate max-w-[7rem]"
                  title={labels[i]}>
                {labels[i]}
              </td>
              {matrix[i].map((val, j) => (
                <HeatmapCell key={j} value={val} isSelf={i === j} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Colour legend ─────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px] text-text-muted">
      <span className="mr-1">Correlation:</span>
      {[
        { label: '≥0.90', cls: 'bg-red-700' },
        { label: '≥0.75', cls: 'bg-red-500/80' },
        { label: '≥0.50', cls: 'bg-orange-500/70' },
        { label: '≥0.25', cls: 'bg-yellow-600/60' },
        { label: '≥0',    cls: 'bg-zinc-600' },
        { label: '<0',    cls: 'bg-emerald-500/70' },
      ].map(l => (
        <div key={l.label} className="flex items-center gap-1">
          <div className={clsx('w-4 h-3 rounded-sm', l.cls)} />
          <span>{l.label}</span>
        </div>
      ))}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

// Lazy tab content re-homed from the former standalone /app/correlation-engine page
const CorrelationEngineTab = lazy(() => import('./CorrelationEnginePage'))

type CorrelationTab = 'scanner' | 'engine'

const CORRELATION_TABS: { key: CorrelationTab; label: string }[] = [
  { key: 'scanner', label: 'Scanner' },
  { key: 'engine',  label: 'Correlation Engine' },
]

export default function CorrelationPage() {
  const {
    tickers, period, threshold,
    setTickers, setPeriod, setThreshold,
    fetchMatrix, fetchPairs, fetchDivergence, fetchSectorMatrix,
    matrixLoading, pairsLoading,
  } = useCorrelation()

  const [tickerInput, setTickerInput] = useState(tickers)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<CorrelationTab>(() => {
    const t = searchParams.get('tab')
    return CORRELATION_TABS.some(x => x.key === t) ? (t as CorrelationTab) : 'scanner'
  })

  // Load sector matrix on mount
  useEffect(() => {
    fetchSectorMatrix()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompute = useCallback(() => {
    setTickers(tickerInput)
    fetchMatrix()
  }, [tickerInput, setTickers, fetchMatrix])

  const handleFindPairs = useCallback(() => {
    fetchPairs()
  }, [fetchPairs])

  const handleSelectPair = useCallback((t1: string, t2: string) => {
    fetchDivergence(t1, t2)
    // Scroll to divergence section smoothly
    setTimeout(() => {
      document.getElementById('divergence-section')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }, [fetchDivergence])

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
            <GitCompare size={18} className="text-violet-400" aria-hidden />
          </div>
          <div>
            <h1 className="page-title">Correlation Scanner</h1>
            <p className="text-xs text-text-muted">Find correlated pairs and divergence opportunities</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <button
            type="button"
            onClick={() => navigateToTerminalCommand(navigate, '/pairs')}
            className="px-2 py-1 rounded bg-surface-card border border-border hover:text-text-primary hover:border-blue/30 transition-colors font-mono"
          >
            /pairs
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border flex-wrap">
        {CORRELATION_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={
              tab === t.key
                ? 'px-4 py-2.5 text-sm font-medium border-b-2 border-blue-light text-blue-light -mb-px'
                : 'px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text-secondary border-b-2 border-transparent -mb-px'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Correlation Engine tab (ex-/app/correlation-engine page, content unchanged) */}
      {tab === 'engine' && (
        <Suspense fallback={<div className="p-8 text-text-muted">Loading correlation engine...</div>}>
          <CorrelationEngineTab />
        </Suspense>
      )}

      {tab === 'scanner' && (<>
      {/* Controls */}
      <div className="glass rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Ticker input */}
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-text-muted mb-1 block">Tickers (comma-separated, max 20)</label>
            <div className="flex gap-2">
              <input
                value={tickerInput}
                onChange={e => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleCompute()}
                placeholder="AAPL,MSFT,GOOGL,AMZN,NVDA"
                className="input flex-1 font-mono"
              />
              <button
                type="button"
                onClick={handleCompute}
                disabled={matrixLoading}
                className="btn-primary text-sm"
              >
                {matrixLoading
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Search size={14} />
                }
                Compute
              </button>
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="text-xs text-text-muted mb-1 block">Period</label>
            <div className="flex gap-1">
              {PERIODS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={clsx(
                    'px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
                    period === p
                      ? 'bg-violet-500/20 text-violet-400'
                      : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Threshold */}
          <div>
            <label className="text-xs text-text-muted mb-1 block">
              Correlation Threshold: <span className="text-text-primary font-mono">{threshold.toFixed(2)}</span>
            </label>
            <input
              type="range"
              min="0.5"
              max="0.99"
              step="0.01"
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              className="w-32 accent-violet-500"
            />
          </div>

          {/* Find Pairs button */}
          <button
            type="button"
            onClick={handleFindPairs}
            disabled={pairsLoading}
            className="btn-secondary text-sm text-violet-400"
          >
            {pairsLoading
              ? <Loader2 size={14} className="animate-spin" />
              : <GitCompare size={14} />
            }
            Find Pairs
          </button>
        </div>
      </div>

      {/* Heatmap */}
      <section className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="panel-title">Correlation Heatmap</h2>
          <button
            type="button"
            onClick={fetchMatrix}
            className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted hover:text-text-primary transition-colors"
            aria-label="Refresh matrix"
          >
            <RefreshCw size={13} className={matrixLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <CorrelationHeatmap />
          <Legend />
        </div>
      </section>

      {/* Two-column: Pairs + Divergence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 stagger-children">
        {/* Pairs list */}
        <section className="card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h2 className="panel-title">Highly Correlated Pairs</h2>
            <button
              type="button"
              onClick={handleFindPairs}
              className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted hover:text-text-primary transition-colors"
              aria-label="Refresh pairs"
            >
              <RefreshCw size={13} className={pairsLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="p-4">
            <PairsList onSelectPair={handleSelectPair} />
          </div>
        </section>

        {/* Divergence */}
        <section id="divergence-section" className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <h2 className="panel-title">Pair Divergence Analysis</h2>
            <p className="text-xs text-text-muted mt-0.5">
              Z-score of spread. Z &gt; +2σ or Z &lt; −2σ = mean-reversion signal.
            </p>
          </div>
          <div className="p-4">
            <DivergencePanel />
          </div>
        </section>
      </div>

      {/* Sector correlation */}
      <section className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <h2 className="panel-title">Sector Correlation Matrix</h2>
            <p className="text-xs text-text-muted mt-0.5">GICS sector ETFs — 11 sectors</p>
          </div>
          <button
            type="button"
            onClick={fetchSectorMatrix}
            className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted hover:text-text-primary transition-colors"
            aria-label="Refresh sector matrix"
          >
            <RefreshCw size={13} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <SectorCorrelationMatrix />
          <Legend />
        </div>
      </section>
      </>)}
    </div>
  )
}
